import { faMusic } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormatDateIso } from "utils/dateUtils";
import CancelIcon from "@mui/icons-material/Cancel";
import { grey } from "@mui/material/colors";
import { IAccountPayablesWithPayments } from "Dashboard/scenes/Partners/domain";
import {
  setIndexesForPaymentDeletion,
  updateAccountPayableAlarm,
  updatePaidPayment,
  updatePaidPaymentTemporarily,
} from "../accountPayable.slice";
import { useAppDispatch } from "hooks/hooks";
import ToolTipPortal from "components/ToolTipPortal/ToolTipPortal";
import DatePicker from "react-datepicker";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { updatePartnersAlarm } from "../../Main/main.slice";
import InfoTooltip from "components/Popup/InfoTooltip";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { PARTNER_MODULE_NAME } from "Dashboard/scenes/Partners/domain/PartnerModule";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import { IUpdatePaidPaymentRequestParams } from "Dashboard/scenes/Partners/domain/receivables/IUpdatePaidPaymentRequestParams";
import NumberInput from "Dashboard/components/NumberInput";
import { getVatRatePresentation } from "Dashboard/components/VatSelect";

interface IProps {
  accountIndex: number;
  item: IAccountPayablesWithPayments;
}

const PaidPayments = ({ item, accountIndex }: IProps) => {
  const dispatch = useAppDispatch();
  const { user } = useSelector(selectStateAuth);

  const initialState = {
    isOpen: false,
    top: 0,
    left: 0,
  };

  const [alarmTooltip, setAlarmTooltip] = useState(initialState);
  const [selectedAlarmDate, setSelectedAlarmDate] = useState<string>(
    item.alarm?.date || ""
  );
  const [selectedAlarmNote, setSelectedAlarmNote] = useState<string>(
    item.alarm?.note || ""
  );
  const [selectedPayableId, setSelectedPayableId] = useState<number>(
    item.accountPayable.payableId
  );

  const close = () => {
    setAlarmTooltip(initialState);

    if (selectedAlarmDate && selectedAlarmNote && selectedPayableId) {
      dispatch(
        updateAccountPayableAlarm({
          date: selectedAlarmDate,
          note: selectedAlarmNote,
          payableId: selectedPayableId,
        })
      );
      toast.success("Uspesno ste podesili alarm!");
      dispatch(updatePartnersAlarm(
        { partnerId: item.accountPayable.partnerId, hasAlarm: true })
      );
    }
  };

  const onChangeBasicField = (data: IUpdatePaidPaymentRequestParams) => {
    dispatch(updatePaidPaymentTemporarily(data));
    updateFieldWithDelay(data);
  };

  const updateFieldWithDelay = useMemo(
    () => debounce(async (data: IUpdatePaidPaymentRequestParams) => {
      try {
        await dispatch(updatePaidPayment(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000),
    [dispatch]
  );

  const handleAlarmIconClick = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    setAlarmTooltip({
      isOpen: true,
      top: e.clientY,
      left: e.clientX,
    });
  };

  const parseDateField = () => {
    const dateValue = selectedAlarmDate || null;
    return dateValue ? new Date(Date.parse(dateValue as string)) : null;
  };

  return (
    <>
      {item.paidPayments.map((payment, paymentIndex) => (
        <div className="row" key={payment.paidPaymentId}>
          <div className="col center">
            {hasFullAccessTo(user, PARTNER_MODULE_NAME)
              ? <InfoTooltip tooltip="Obriši zapis" classesProp="btn delete-btn" element="span">
                <button
                  className="btn delete-btn"
                  onClick={() => {
                    dispatch(
                      setIndexesForPaymentDeletion({ accountIndex, paymentIndex })
                    );
                  }}
                >
                  <CancelIcon sx={{ color: grey[300] }} fontSize="small" />
                </button>
              </InfoTooltip>
              : undefined}
          </div>
          <div className="col center"></div>
          <div className="col center">
            <div className="bg-item bg-item-border-light center w80">
              <span className="w80">{item.accountPayable.invoiceId}</span>
            </div>
          </div>
          <div className="col center" />
          <div className="col center">
            <div className="bg-item bg-transparent">
              <DatePicker
                popperProps={{ strategy: "fixed" }}
                className="date-picker bg-item-border-light light bg-transparent"
                showMonthDropdown
                showYearDropdown
                onChange={(date) => {
                  if (date) {
                    onChangeBasicField({
                      paidPaymentId: payment.paidPaymentId,
                      accountPayableId: payment.accountPayableId,
                      date: FormatDateIso(date)
                    });
                  }
                }}
                selected={payment.date
                  ? new Date(payment.date) : null}
                dateFormat="dd.MM.yy"
                disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
              />
            </div>
          </div>
          <div className="col center" />
          <div className="col center" />
          <div className="col center">
            <div className="bg-item bg-transparent right">
              <span className="light">keš&nbsp;</span>
              <input type="checkbox"
                checked={payment.isCash}
                onChange={hasWriteAccessTo(user, PARTNER_MODULE_NAME)
                  ? (e) => {
                    onChangeBasicField({
                      paidPaymentId: payment.paidPaymentId,
                      accountPayableId: payment.accountPayableId,
                      isCash: e.target.checked
                    });
                  }
                  : undefined}
                disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
              />
            </div>
          </div>
          <div className="col center">
            <NumberInput
              formatAs="quantity"
              onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                if (parsedValue !== payment.amount) {
                  onChangeBasicField({
                    paidPaymentId: payment.paidPaymentId,
                    accountPayableId: payment.accountPayableId,
                    amount: parsedValue
                  });
                }
              }}
              value={`${payment.amount}`}
              className="bg-item bg-item-border-light light center bg-transparent"
              disabled={!hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
            />
          </div>
          <div className="col center">
            <div className="bg-item bg-transparent center">
              <InfoTooltip tooltip="Alarm" classesProp="btn plus-btn" element="span">
                <button
                  className={`btn plus-btn music ${item.alarm ? "darker" : ""}`}
                  onClick={(e) => {
                    handleAlarmIconClick(e);
                  }}
                >
                  <FontAwesomeIcon icon={faMusic} />
                </button>
              </InfoTooltip>
            </div>
          </div>
          <div className="col center">
            <div className="bg-item bg-item-border-light center">
              <span className="light">{getVatRatePresentation(item.accountPayable.vatRate)}</span>
            </div>
          </div>
          <div className="col center" />
        </div>
      ))}
      <ToolTipPortal
        {...alarmTooltip}
        onClose={close}
        text="Podesi ALARM"
        offset={{ top: -130, left: 10 }}
      >
        <div className="alarm-div">
          <div>
            <DatePicker
              className="datePicker"
              dateFormat="dd.MM.yyyy"
              showMonthDropdown
              showYearDropdown
              onChange={(d: any) => setSelectedAlarmDate(d)}
              selected={parseDateField()}
            />
            {selectedAlarmDate !== "" && (
              <CancelIcon
                className="clearDate"
                onClick={() => {
                  dispatch(updateAccountPayableAlarm({ date: '', note: '', payableId: selectedPayableId }));
                  setAlarmTooltip(initialState);
                  setSelectedAlarmDate('');
                  setSelectedAlarmNote('');
                  dispatch(updatePartnersAlarm(
                    { partnerId: item.accountPayable.partnerId, hasAlarm: false })
                  );
                  toast.success("Uspesno ste izbrisali alarm!")
                }}
                sx={{ color: grey[300], fontSize: 17 }}
              />
            )}
          </div>

          <textarea
            onChange={(e) => setSelectedAlarmNote(e.target.value)}
            rows={10}
            defaultValue={selectedAlarmNote}
          ></textarea>
        </div>
      </ToolTipPortal>
    </>
  );
};

export default PaidPayments;
